<!-- src/components/CreateMealModal.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_RECIPES, GET_MEAL_TYPES } from '../graphql/queries'
import { CREATE_MEAL } from '../graphql/mutations'
import { showSuccess, showError } from '../utils/errorHandling'
import { keyToUtcIso, buildScheduledDates } from '../utils/dateHelpers'
import FormErrorSummary from './FormErrorSummary.vue'
import { vm } from '../utils/validationMessages'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import PortionsModeToggle from './PortionsModeToggle.vue'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'
import dayjs from 'dayjs'

interface Props {
  show: boolean
  preselectedRecipe?: any
  preselectedDate?: string // YYYY-MM-DD
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)
const emit = defineEmits<{
  close: []
  created: [meal: any]
}>()

// ── Step state ───────────────────────────────────────────────────────────────
const currentStep = ref(1)

// ── Step 1: Select Recipe ────────────────────────────────────────────────────
const searchQuery = ref('')
const debouncedSearch = ref('')
const currentPage = ref(1)
const PAGE_SIZE = 5
const selectedRecipe = ref<any>(null)

let searchTimer: ReturnType<typeof setTimeout>
watch(searchQuery, (val) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val
    currentPage.value = 1
  }, 300)
})

// ── Step 2: Date & Time ──────────────────────────────────────────────────────
const calendarMonth = ref(dayjs().startOf('month'))
// Array of 'YYYY-MM-DD' keys — multi-select, any order
const selectedDateKeys = ref<string[]>([])
const selectedTypeId = ref('')
const timeInput = ref('')
// Tracks whether the user has typed a time themselves, so auto-fill from the
// meal type never overwrites a manual entry.
const timeManuallySet = ref(false)
const quickFillCount = ref(2)
const portionsMode = ref<'leftovers' | 'repeat'>('leftovers')

// Sorted keys (chronological) — primary date is always [0]
const sortedDateKeys = computed(() => [...selectedDateKeys.value].sort())

// ── Unsaved changes ──────────────────────────────────────────────────────────
const showUnsavedChangesDialog = ref(false)

// ── Validation ────────────────────────────────────────────────────────────────
const step1NextAttempted = ref(false)
const step2NextAttempted = ref(false)

const step2DateError = computed(() =>
  step2NextAttempted.value && selectedDateKeys.value.length === 0
    ? vm.date.required
    : ''
)
const step2TypeError = computed(() =>
  step2NextAttempted.value && !selectedTypeId.value
    ? vm.mealType.required
    : ''
)

const step1ErrorSummary = computed(() => {
  if (!step1NextAttempted.value || selectedRecipe.value) return []
  return [{ label: 'Recipe', message: vm.recipe.required }]
})

const step2ErrorSummary = computed(() => {
  const items = []
  if (step2DateError.value) items.push({ label: 'Date', message: step2DateError.value })
  if (step2TypeError.value) items.push({ label: 'Meal Type', message: step2TypeError.value })
  return items
})

// ── GraphQL queries ──────────────────────────────────────────────────────────
const { result: recipesResult, loading: loadingRecipes } = useQuery(
  GET_RECIPES,
  computed(() => ({ limit: 100, page: 0, search: debouncedSearch.value || undefined })),
  { fetchPolicy: 'cache-and-network' },
)

const { result: mealTypesResult } = useQuery(GET_MEAL_TYPES)

const { mutate: createMeal, loading: mutationLoading } = useMutation(CREATE_MEAL, {
  update(cache, { data: { createMeal: newMeal } }) {
    cache.modify({
      fields: {
        meals(existingMeals = []) {
          return [...existingMeals, newMeal]
        },
      },
    })
  },
})

// ── Computed ─────────────────────────────────────────────────────────────────
const allRecipes = computed(() => recipesResult.value?.recipes || [])
const mealTypes = computed(() => mealTypesResult.value?.mealTypes || [])

const pagedRecipes = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return allRecipes.value.slice(start, start + PAGE_SIZE)
})

const totalPages = computed(() => Math.max(1, Math.ceil(allRecipes.value.length / PAGE_SIZE)))

const selectedTypeName = computed(() => mealTypes.value.find((m: any) => m.id === selectedTypeId.value)?.name || '')

// Format a meal type's "HH:MM" (24h) time into the modal's "h:mm A" display format.
const formatTypeTime = (t?: string | null): string => {
  if (!t) return ''
  const [hStr, mStr] = t.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  if (Number.isNaN(h) || Number.isNaN(m)) return ''
  const ampm = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

// When the meal type changes, fill the Time from the type's associated time —
// but only if the user hasn't entered a time themselves.
watch(selectedTypeId, (id) => {
  if (timeManuallySet.value) return
  const type = mealTypes.value.find((m: any) => m.id === id)
  const formatted = formatTypeTime(type?.time)
  if (formatted) timeInput.value = formatted
})

const hasUnsavedChanges = computed(
  () => selectedRecipe.value !== null || selectedDateKeys.value.length > 0 || selectedTypeId.value !== '',
)

const canGoToStep2 = computed(() => selectedRecipe.value !== null)
const canGoToStep3 = computed(() => selectedDateKeys.value.length > 0 && selectedTypeId.value !== '')

// ── Calendar ─────────────────────────────────────────────────────────────────
const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const calendarDays = computed(() => {
  const start = calendarMonth.value
  const startDow = start.day() // 0=Sun
  const startMon = (startDow + 6) % 7 // Mon=0
  const daysInMonth = start.daysInMonth()
  const days: { date: dayjs.Dayjs; currentMonth: boolean }[] = []

  for (let i = startMon - 1; i >= 0; i--) {
    days.push({ date: start.subtract(i + 1, 'day'), currentMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: start.date(d), currentMonth: true })
  }
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: start.add(daysInMonth - 1 + i, 'day'), currentMonth: false })
  }
  return days
})

// ── Methods ──────────────────────────────────────────────────────────────────
const resetForm = () => {
  currentStep.value = 1
  searchQuery.value = ''
  debouncedSearch.value = ''
  currentPage.value = 1
  selectedRecipe.value = null
  calendarMonth.value = dayjs().startOf('month')
  selectedDateKeys.value = []
  selectedTypeId.value = ''
  timeInput.value = ''
  timeManuallySet.value = false
  quickFillCount.value = 2
  portionsMode.value = 'leftovers'
  step1NextAttempted.value = false
  step2NextAttempted.value = false
}

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  resetForm()
  showUnsavedChangesDialog.value = false
  emit('close')
}

const nextStep = () => {
  if (currentStep.value === 1) {
    step1NextAttempted.value = true
    if (canGoToStep2.value) currentStep.value = 2
  } else if (currentStep.value === 2) {
    step2NextAttempted.value = true
    if (canGoToStep3.value) currentStep.value = 3
  }
}

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--
}

const getInitials = (title: string) =>
  title
    .split(' ')
    .filter((w) => w && /^[A-ZÀ-Ú]/u.test(w))
    .map((w) => w[0].toUpperCase())
    .slice(0, 3)
    .join('')

const isSelectedDay = (date: dayjs.Dayjs) => selectedDateKeys.value.includes(date.format('YYYY-MM-DD'))
const isPrimaryDay = (date: dayjs.Dayjs) => sortedDateKeys.value[0] === date.format('YYYY-MM-DD')
const isToday = (date: dayjs.Dayjs) => dayjs().isSame(date, 'day')

const toggleDate = (date: dayjs.Dayjs, currentMonth: boolean) => {
  if (!currentMonth) return
  const key = date.format('YYYY-MM-DD')
  const idx = selectedDateKeys.value.indexOf(key)
  if (idx >= 0) {
    selectedDateKeys.value.splice(idx, 1)
  } else {
    selectedDateKeys.value.push(key)
  }
}

const removeDate = (key: string) => {
  const idx = selectedDateKeys.value.indexOf(key)
  if (idx >= 0) selectedDateKeys.value.splice(idx, 1)
}

const fillConsecutive = () => {
  if (sortedDateKeys.value.length === 0) return
  const primary = dayjs(sortedDateKeys.value[0])
  const existing = new Set(selectedDateKeys.value)
  for (let i = 1; i < quickFillCount.value; i++) {
    existing.add(primary.add(i, 'day').format('YYYY-MM-DD'))
  }
  selectedDateKeys.value = [...existing]
}

const submitMeal = async () => {
  try {
    const sorted = sortedDateKeys.value
    const primaryKey = sorted[0]

    let primaryIso: string
    if (timeInput.value) {
      const parsed = dayjs(`${primaryKey} ${timeInput.value}`, ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD HH:mm'])
      primaryIso = parsed.isValid() ? parsed.utcOffset(0, true).toISOString() : keyToUtcIso(primaryKey)
    } else {
      primaryIso = keyToUtcIso(primaryKey)
    }

    const scheduledDates = buildScheduledDates(sorted, primaryIso)

    const result = await createMeal({
      title: selectedRecipe.value.title,
      description: selectedRecipe.value.description ?? null,
      recipe_id: selectedRecipe.value.id,
      type_id: selectedTypeId.value,
      scheduled_date: primaryIso,
      scheduled_dates: scheduledDates,
      portions_days: scheduledDates.length,
      portions_mode: scheduledDates.length > 1 ? portionsMode.value : undefined,
    })

    showSuccess('Meal added to your plan!')
    emit('created', result?.data?.createMeal)
    closeModal()
  } catch (e) {
    showError(e, 'create meal')
  }
}

watch(
  () => props.show,
  (show) => {
    if (!show) {
      resetForm()
    } else {
      if (props.preselectedDate) {
        const d = dayjs(props.preselectedDate)
        calendarMonth.value = d.startOf('month')
        selectedDateKeys.value = [d.format('YYYY-MM-DD')]
      }
      if (props.preselectedRecipe) {
        selectedRecipe.value = props.preselectedRecipe
        currentStep.value = props.preselectedDate ? 2 : 2
      }
    }
  },
)
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="attemptClose">
      <div
        class="bg-white rounded-lg w-full max-w-[720px] flex flex-col overflow-hidden modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-meal-title"
      >
        <!-- ── Header ──────────────────────────────────────────────────────── -->
        <div class="flex-shrink-0 flex items-center justify-between px-6 modal-hd">
          <h2 id="create-meal-title" class="font-bold text-[#0F172A] modal-title">New Meal</h2>
          <button @click="attemptClose" class="modal-close-button" aria-label="Close modal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- ── Step Indicator ──────────────────────────────────────────────── -->
        <div class="flex-shrink-0 px-6 pt-6 step-bar">
          <div class="flex items-stretch gap-2 sm:gap-4">
            <div
              class="flex items-center gap-2 pb-3 border-b-2 transition-all"
              :class="currentStep === 1 ? 'border-[#4F8646]' : 'border-transparent opacity-50'"
            >
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                :class="currentStep >= 1 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'"
              >
                1
              </div>
              <span
                class="hidden sm:inline text-sm font-bold whitespace-nowrap"
                :class="currentStep === 1 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Select Recipe</span
              >
              <span class="sm:hidden text-xs font-bold" :class="currentStep === 1 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Recipe</span
              >
            </div>
            <div class="flex-1 flex items-center pb-3"><div class="step-sep h-px w-full"></div></div>
            <div
              class="flex items-center gap-2 pb-3 border-b-2 transition-all"
              :class="currentStep === 2 ? 'border-[#4F8646]' : 'border-transparent opacity-50'"
            >
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                :class="currentStep >= 2 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'"
              >
                2
              </div>
              <span
                class="hidden sm:inline text-sm font-bold whitespace-nowrap"
                :class="currentStep === 2 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Date &amp; Time</span
              >
              <span class="sm:hidden text-xs font-bold" :class="currentStep === 2 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Date</span
              >
            </div>
            <div class="flex-1 flex items-center pb-3"><div class="step-sep h-px w-full"></div></div>
            <div
              class="flex items-center gap-2 pb-3 border-b-2 transition-all"
              :class="currentStep === 3 ? 'border-[#4F8646]' : 'border-transparent opacity-50'"
            >
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                :class="currentStep >= 3 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'"
              >
                3
              </div>
              <span
                class="hidden sm:inline text-sm font-bold whitespace-nowrap"
                :class="currentStep === 3 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Confirm</span
              >
              <span class="sm:hidden text-xs font-bold" :class="currentStep === 3 ? 'text-[#4F8646]' : 'text-[#64748B]'"
                >Confirm</span
              >
            </div>
          </div>
        </div>

        <!-- ── Body ────────────────────────────────────────────────────────── -->
        <div class="flex-1 overflow-y-auto min-h-0 p-6">
          <!-- ── STEP 1: Select Recipe ─────────────────────────────────────── -->
          <div v-if="currentStep === 1" class="flex flex-col h-full gap-6">
            <!-- Search bar -->
            <div class="relative flex-shrink-0">
              <svg
                class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] icon-sm"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
                />
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search your recipes (e.g. Pasta, Salad...)"
                class="w-full rounded-lg text-sm text-[#0F172A] placeholder-[#6B7280] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all search-input"
              />
            </div>

            <FormErrorSummary :errors="step1ErrorSummary" class="flex-shrink-0" />

            <!-- Recipe list -->
            <div class="flex-1 flex flex-col gap-4 overflow-y-auto min-h-0">
              <div v-if="loadingRecipes && allRecipes.length === 0" class="text-center py-10 text-sm text-[#64748B]">
                Loading recipes...
              </div>
              <div v-else-if="pagedRecipes.length === 0" class="text-center py-10 text-sm text-[#64748B]">
                No recipes found
              </div>

              <button
                v-for="recipe in pagedRecipes"
                :key="recipe.id"
                @click="selectedRecipe = recipe"
                class="w-full flex items-center gap-4 rounded-lg text-left transition-all recipe-option"
                :class="{ 'recipe-option--selected': selectedRecipe?.id === recipe.id }"
              >
                <div
                  class="flex-shrink-0 rounded-lg flex items-center justify-center recipe-avatar"
                  :class="{ 'recipe-avatar--selected': selectedRecipe?.id === recipe.id }"
                >
                  <span
                    class="font-bold select-none recipe-initials"
                    :class="{ 'recipe-initials--selected': selectedRecipe?.id === recipe.id }"
                  >
                    {{ getInitials(recipe.title) }}
                  </span>
                </div>
                <div class="flex-1 min-w-0 flex flex-col gap-1">
                  <p class="font-bold text-[#0F172A] truncate recipe-item-title">{{ recipe.title }}</p>
                  <p v-if="recipe.description" class="text-xs text-[#64748B] truncate">{{ recipe.description }}</p>
                  <div class="flex flex-wrap gap-1.5 mt-0.5">
                    <span
                      v-if="recipe.category"
                      class="inline-flex items-center text-white text-xs font-normal px-2 recipe-category-tag"
                    >
                      {{ recipe.category.name }}
                    </span>
                  </div>
                </div>
                <div
                  class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all recipe-checkbox"
                  :class="{ 'recipe-checkbox--selected': selectedRecipe?.id === recipe.id }"
                >
                  <svg
                    v-if="selectedRecipe?.id === recipe.id"
                    class="w-3 h-3"
                    fill="none"
                    stroke="white"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </button>
            </div>

            <!-- Pagination -->
            <div class="flex-shrink-0 flex items-center justify-between">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md pagination-btn"
              >
                <svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
              <span class="pagination-label">Page {{ currentPage }} / {{ totalPages }}</span>
              <button
                @click="currentPage++"
                :disabled="currentPage >= totalPages"
                class="flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md pagination-btn"
              >
                Next
                <svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <!-- ── STEP 2: Date & Time ────────────────────────────────────────── -->
          <div v-else-if="currentStep === 2" class="flex flex-col gap-6">
            <FormErrorSummary :errors="step2ErrorSummary" />
            <!-- Select Date -->
            <div class="flex flex-col gap-3">
              <div>
                <h3 class="font-bold text-[#0F172A] step-section-title">Schedule Days</h3>
                <p class="text-sm text-[#64748B] mt-1">
                  Pick one day, or tap several days to plan this meal across multiple days — they don't need to be
                  consecutive.
                </p>
              </div>
              <div class="rounded-lg calendar-wrap">
                <div class="flex items-center justify-between mb-4">
                  <button
                    @click="calendarMonth = calendarMonth.subtract(1, 'month')"
                    class="p-1.5 rounded-lg transition-colors hover:bg-slate-100 text-[#64748B]"
                    aria-label="Previous month"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span class="font-bold text-[#0F172A] calendar-month-label">{{
                    calendarMonth.format('MMMM YYYY')
                  }}</span>
                  <button
                    @click="calendarMonth = calendarMonth.add(1, 'month')"
                    class="p-1.5 rounded-lg transition-colors hover:bg-slate-100 text-[#64748B]"
                    aria-label="Next month"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div class="grid grid-cols-7 mb-1">
                  <div v-for="d in WEEK_DAYS" :key="d" class="text-center font-bold text-[#94A3B8] calendar-day-hdr">
                    {{ d }}
                  </div>
                </div>
                <div class="grid grid-cols-7">
                  <div
                    v-for="day in calendarDays"
                    :key="day.date.toISOString()"
                    class="flex items-center justify-center calendar-cell"
                  >
                    <button
                      @click="toggleDate(day.date, day.currentMonth)"
                      class="w-8 h-8 flex items-center justify-center text-sm transition-colors"
                      :class="[
                        !day.currentMonth ? 'cursor-default' : 'cursor-pointer',
                        isSelectedDay(day.date) && isPrimaryDay(day.date)
                          ? 'rounded-lg font-semibold text-white cal-day--primary'
                          : isSelectedDay(day.date)
                            ? 'rounded-lg font-semibold cal-day--additional'
                            : isToday(day.date)
                              ? 'rounded-lg font-medium cal-day--today'
                              : day.currentMonth
                                ? 'rounded-md hover:bg-slate-100 cal-day--current'
                                : 'cal-day--other',
                      ]"
                    >
                      {{ day.date.date() }}
                    </button>
                  </div>
                </div>

                <!-- Empty-state hint — no dates selected yet -->
                <div v-if="selectedDateKeys.length === 0 && !step2DateError" class="mt-3 cal-empty-hint">
                  <svg class="cal-hint-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                    />
                  </svg>
                  <span>Tap any day to select it. Tap more days to schedule across multiple days.</span>
                </div>

                <!-- Single-date nudge -->
                <div v-else-if="selectedDateKeys.length === 1" class="mt-3 cal-nudge">
                  <div class="flex flex-wrap gap-1.5 mb-2">
                    <span
                      v-for="key in sortedDateKeys"
                      :key="key"
                      class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold date-chip date-chip--primary"
                    >
                      {{ dayjs(key).format('D MMM') }}
                      <button @click="removeDate(key)" class="date-chip-remove" aria-label="Remove date">×</button>
                    </span>
                  </div>
                  <p class="cal-nudge-text">
                    <svg class="cal-hint-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                    Planning to have this on more days? Tap additional dates above.
                  </p>
                </div>

                <!-- Multi-date chips + quick-fill -->
                <template v-else>
                  <div class="mt-3 flex flex-wrap gap-1.5">
                    <span
                      v-for="key in sortedDateKeys"
                      :key="key"
                      class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold date-chip"
                      :class="sortedDateKeys[0] === key ? 'date-chip--primary' : 'date-chip--extra'"
                    >
                      {{ dayjs(key).format('D MMM') }}
                      <button @click="removeDate(key)" class="date-chip-remove" aria-label="Remove date">×</button>
                    </span>
                  </div>
                  <div class="mt-3 flex items-center gap-2 text-sm">
                    <span class="text-[#64748B]">Quick fill:</span>
                    <input
                      v-model.number="quickFillCount"
                      type="number"
                      min="2"
                      max="14"
                      class="w-14 rounded border border-[#E2E8F0] px-2 py-0.5 text-sm text-center outline-none focus:ring-1 focus:ring-[#4F8646]"
                    />
                    <button
                      @click="fillConsecutive"
                      class="rounded px-2 py-0.5 text-xs font-semibold text-[#4F8646] border border-[#4F8646] hover:bg-[#EAF1EB] transition-colors"
                    >
                      consecutive days from first
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Portions mode — only shown when multiple dates are selected -->
            <div v-if="selectedDateKeys.length > 1" class="flex flex-col gap-2">
              <h3 class="font-bold text-[#0F172A] step-section-title">How does this meal work?</h3>
              <PortionsModeToggle v-model="portionsMode" />
            </div>

            <!-- Meal Details -->
            <div class="flex flex-col gap-4">
              <h3 class="font-bold text-[#0F172A] meal-details-title">Meal Details</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1.5">
                  <label class="font-bold text-[#334155] form-label">
                    Meal Type <span class="text-red-500 ml-0.5" aria-label="required">*</span>
                  </label>
                  <select
                    v-model="selectedTypeId"
                    class="w-full rounded-lg text-[#0F172A] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all bg-white appearance-none px-4 form-field-input"
                    :class="step2TypeError ? 'border-red-500 bg-red-50' : ''"
                    @change="step2NextAttempted && (step2NextAttempted = step2NextAttempted)"
                  >
                    <option value="" disabled>Select type</option>
                    <option v-for="type in mealTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                  </select>
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="font-bold text-[#334155] form-label">
                    Time <span class="font-normal text-[#64748B]">(Optional)</span>
                  </label>
                  <input
                    v-model="timeInput"
                    @input="timeManuallySet = true"
                    type="text"
                    placeholder="12:30 PM"
                    class="w-full rounded-lg text-[#0F172A] placeholder-[#94A3B8] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all form-field-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- ── STEP 3: Confirm ────────────────────────────────────────────── -->
          <div v-else-if="currentStep === 3" class="flex flex-col gap-6">
            <div class="flex flex-col items-center gap-3 text-center">
              <h3 class="font-bold text-[#0F172A] flex items-center gap-2 confirm-heading">
                Looks good!
                <svg class="w-6 h-6 text-[#4F8646]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0"
                  />
                </svg>
              </h3>
              <p class="text-[#475569] confirm-subtitle">Review your meal details before adding it to your plan.</p>
            </div>

            <div class="flex flex-col sm:flex-row items-center sm:items-start gap-4 rounded-lg summary-card">
              <div class="flex-shrink-0 rounded-lg flex items-center justify-center summary-avatar">
                <span class="font-bold select-none summary-initials">{{
                  getInitials(selectedRecipe?.title ?? '')
                }}</span>
              </div>
              <div class="flex-1 w-full grid grid-cols-2 gap-x-4 gap-y-3">
                <div class="flex flex-col gap-1">
                  <p class="font-bold text-[#64748B] uppercase summary-label">Recipe</p>
                  <p class="font-bold text-[#0F172A] summary-value">{{ selectedRecipe?.title }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <p class="font-bold text-[#64748B] uppercase summary-label">Meal Type</p>
                  <p class="font-bold text-[#0F172A] summary-value">{{ selectedTypeName }}</p>
                </div>
                <div class="flex flex-col gap-1 col-span-2">
                  <p class="font-bold text-[#64748B] uppercase summary-label">
                    {{ sortedDateKeys.length > 1 ? `Dates (${sortedDateKeys.length})` : 'Date' }}
                  </p>
                  <div class="flex flex-wrap gap-1 mt-0.5">
                    <span
                      v-for="key in sortedDateKeys"
                      :key="key"
                      class="text-xs font-semibold px-2 py-0.5 rounded-full"
                      :class="sortedDateKeys[0] === key ? 'bg-[#4F8646] text-white' : 'bg-[#EAF1EB] text-[#2d5a26]'"
                    >
                      {{ dayjs(key).format('ddd D MMM') }}
                    </span>
                  </div>
                </div>
                <div v-if="sortedDateKeys.length > 1" class="flex flex-col gap-1">
                  <p class="font-bold text-[#64748B] uppercase summary-label">Shopping list</p>
                  <p class="font-bold text-[#0F172A] summary-value">
                    {{
                      portionsMode === 'leftovers'
                        ? 'Ingredients counted once (cooked once)'
                        : `Ingredients × ${sortedDateKeys.length} days (cook fresh each day)`
                    }}
                  </p>
                </div>
                <div class="flex flex-col gap-1">
                  <p class="font-bold text-[#64748B] uppercase summary-label">Recipe Type</p>
                  <p class="font-bold text-[#0F172A] summary-value">{{ selectedRecipe?.category?.name || '—' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Footer ──────────────────────────────────────────────────────── -->
        <div class="flex-shrink-0 flex items-center justify-between modal-ft">
          <button
            v-if="currentStep === 1"
            @click="attemptClose"
            class="cancel-btn font-semibold transition-colors hover:opacity-80"
          >
            Cancel
          </button>
          <button
            v-else
            @click="prevStep"
            class="flex items-center gap-2 rounded-lg font-bold transition-colors hover:opacity-90 back-btn"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            v-if="currentStep === 1"
            @click="nextStep"
            class="rounded-lg font-bold text-white transition-all primary-action-btn"
            :class="!canGoToStep2 ? 'opacity-60' : 'hover:opacity-90'"
          >
            Next: Date &amp; Time
          </button>
          <button
            v-else-if="currentStep === 2"
            @click="nextStep"
            class="rounded-lg font-bold text-white transition-all primary-action-btn"
            :class="!canGoToStep3 ? 'opacity-60' : 'hover:opacity-90'"
          >
            Next: Confirm
          </button>
          <button
            v-else
            @click="submitMeal"
            :disabled="mutationLoading"
            class="rounded-lg font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed primary-action-btn"
          >
            {{ mutationLoading ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>

    <UnsavedChangesDialog
      :show="showUnsavedChangesDialog"
      @keep-editing="showUnsavedChangesDialog = false"
      @discard="closeModal"
    />
  </Teleport>
</template>

<style scoped>
/* ── Modal shell ── */
.modal-panel {
  max-height: 90vh;
  box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
}
.modal-hd {
  border-bottom: 1px solid #f1f5f9;
  min-height: 64px;
}
.modal-title {
  font-size: 20px;
}
.step-bar {
  border-bottom: 2px solid #f1f5f9;
}
.step-sep {
  background: #e2e8f0;
}
.modal-ft {
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  padding: 24px;
}

/* ── Search ── */
.icon-sm {
  width: 18px;
  height: 18px;
}
.search-input {
  background: #f1f5f9;
  padding: 14px 16px 14px 48px;
  border: none;
}

/* ── Recipe options ── */
.recipe-option {
  padding: 12px;
  background: transparent;
  border: 1px solid #f1f5f9;
}
.recipe-option--selected {
  background: rgba(79, 134, 70, 0.05);
  border-color: #4f8646;
}
.recipe-avatar {
  width: 80px;
  height: 80px;
  background: #e2e8f0;
}
.recipe-avatar--selected {
  background: rgba(79, 134, 70, 0.4);
}
.recipe-initials {
  font-size: 18px;
  letter-spacing: 0.02em;
  color: #94a3b8;
}
.recipe-initials--selected {
  color: #2d5a26;
}
.recipe-item-title {
  font-size: 16px;
  line-height: 1.5;
}
.recipe-category-tag {
  background: #ff9800;
  border-radius: 20px;
  height: 22px;
}
.recipe-checkbox {
  border-color: #e2e8f0;
  background: transparent;
}
.recipe-checkbox--selected {
  border-color: #4f8646;
  background: #4f8646;
}

/* ── Pagination ── */
.pagination-btn {
  background: rgba(79, 134, 70, 0.2);
  padding: 8px;
  height: 29px;
  font-size: 12px;
  color: #000;
  border: none;
  cursor: pointer;
}
.pagination-label {
  font-size: 12px;
  color: #000;
}

/* ── Calendar ── */
.step-section-title {
  font-size: 16px;
}
.calendar-wrap {
  background: rgba(248, 250, 252, 0.5);
  border: 1px solid #e2e8f0;
  padding: 16px;
}
.calendar-month-label {
  font-size: 16px;
}
.calendar-day-hdr {
  font-size: 11px;
  padding: 4px 0;
}
.calendar-cell {
  height: 36px;
}
.cal-day--primary {
  background: #4f8646;
  color: white;
}
.cal-day--additional {
  box-shadow: inset 0 0 0 2px #4f8646;
  color: #2d5a26;
  background: #eaf1eb;
}
.cal-day--today {
  background: rgba(79, 134, 70, 0.1);
  color: #4f8646;
}
.cal-day--other {
  color: #cbd5e1;
}
.cal-day--current {
  color: #0f172a;
}

/* ── Empty-state hint (no dates selected) ── */
.cal-empty-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}
/* ── Single-date nudge ── */
.cal-nudge-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
  font-style: italic;
}
.cal-hint-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.7;
}

/* ── Date chips ── */
.date-chip {
  border: 1px solid transparent;
  cursor: default;
}
.date-chip--primary {
  background: #4f8646;
  color: white;
  border-color: #4f8646;
}
.date-chip--extra {
  background: #eaf1eb;
  color: #2d5a26;
  border-color: #b4cbb0;
}
.date-chip-remove {
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
}
.date-chip-remove:hover {
  opacity: 1;
}

/* ── Form fields ── */
.meal-details-title {
  font-size: 18px;
  letter-spacing: -0.015em;
}
.form-label {
  font-size: 14px;
}
.form-field-input {
  border: 1px solid #e2e8f0;
  height: 42px;
  font-size: 16px;
  padding: 8px 16px;
}

/* ── Confirm step ── */
.confirm-heading {
  font-size: 28px;
}
.confirm-subtitle {
  font-size: 16px;
}
.summary-card {
  background: #f8fafc;
  padding: 20px;
}
.summary-avatar {
  width: 80px;
  height: 80px;
  background: #b4cbb0;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}
.summary-initials {
  font-size: 22px;
  letter-spacing: 0.02em;
  color: #2d5a26;
}
.summary-label {
  font-size: 10px;
  letter-spacing: 0.05em;
}
.summary-value {
  font-size: 14px;
}

/* ── Footer buttons ── */
.cancel-btn {
  font-size: 14px;
  color: #475569;
  padding: 10px 24px;
  background: none;
  border: none;
  cursor: pointer;
}
.back-btn {
  background: rgba(79, 134, 70, 0.2);
  color: #334155;
  padding: 10px 24px;
  font-size: 14px;
  border: none;
  cursor: pointer;
}
.primary-action-btn {
  background: #4f8646;
  padding: 10px 32px;
  font-size: 14px;
  box-shadow:
    0px 4px 6px -4px rgba(79, 134, 70, 0.3),
    0px 10px 15px -3px rgba(79, 134, 70, 0.3);
  border: none;
  cursor: pointer;
}
</style>
